import { useCallback, useEffect, useRef, useState } from 'react';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import type { JanusCallStatus, SubscriberFeed, UseJanusCallOptions, VideoRoomPublisher } from './types';

export interface JanusSessionBag {
  r: JanusCallRefs;
  set: JanusCallSetters;
}

export interface JanusSessionState {
  r: JanusCallRefs;
  set: JanusCallSetters;
  
  getSession: () => JanusSessionBag;
  callStatus: JanusCallStatus;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  error: string | null;
  cleanupOnUnmount: boolean;
}

export function useJanusSession(options?: UseJanusCallOptions): JanusSessionState {
  const cleanupOnUnmount = options?.cleanupOnUnmount ?? false;

  const onAudioHardwareLostRef = useRef(options?.onAudioHardwareLost);
  const translateCallRef = useRef(options?.translateCall);
  const onMediaConnectionLostRef = useRef(options?.onMediaConnectionLost);
  const onRemoteCallEndedRef = useRef(options?.onRemoteCallEnded);
  const onLocalAudioRecoveredRef = useRef(options?.onLocalAudioRecovered);
  const onRemotePlaybackRecoveredRef = useRef(options?.onRemotePlaybackRecovered);
  const onNetworkReconnectFailedRef = useRef(options?.onNetworkReconnectFailed);
  const recoverRemotePlaybackRef = useRef<() => Promise<boolean>>(async () => false);
  const mediaConnectionLostSignaledRef = useRef(false);
  const deviceChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsReconnectAttemptsRef = useRef(0);
  const wsReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signalingResyncInFlightRef = useRef(false);
  const reconnectInFlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalWsCloseRef = useRef(false);
  const pendingAttachAfterCreateRef = useRef<'attach' | 'attach-reconnect'>('attach');
  const ghostSessionIdRef = useRef<number | null>(null);
  const mediaReconnectPendingRef = useRef(false);
  const scheduleWebSocketReconnectRef = useRef<(immediate?: boolean) => void>(() => undefined);
  const reconnectWebSocketAndIceRef = useRef<() => Promise<void>>(async () => undefined);
  const recoverLocalAudioRef = useRef<() => Promise<boolean>>(async () => false);
  const refreshInboundMediaRef = useRef<() => void>(() => undefined);

  const [callStatus, setCallStatus] = useState<JanusCallStatus>('idle');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<number | null>(null);
  const handleIdRef = useRef<number | null>(null);
  const publisherPcRef = useRef<RTCPeerConnection | null>(null);
  const publisherPendingCandidatesRef = useRef<RTCIceCandidate[]>([]);
  const isSessionReadyRef = useRef(false);
  const micDisconnectHandledRef = useRef(false);
  const isPublisherConfiguredRef = useRef(false);
  const isPublisherJoinedRef = useRef(false);
  const isJoinedRef = useRef(false);
  const signalingUrlRef = useRef<string>('');
  const roomIdRef = useRef<string>('');
  const myPublisherIdRef = useRef<number | string | null>(null);
  const myPrivateIdRef = useRef<number | null>(null);
  const displayNameRef = useRef<string>('');
  const subscriberFeedsRef = useRef<SubscriberFeed[]>([]);
  const subscribedFeedIdsRef = useRef<Set<number | string>>(new Set());
  const networkDropPendingRef = useRef(false);
  const pendingLocalStreamRefreshRef = useRef(false);
  const networkBlipAtRef = useRef(0);
  const pendingSubscriberAttachesRef = useRef<Map<string, number | string>>(new Map());
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const joinRoomResolveRef = useRef<(() => void) | null>(null);
  const joinRoomRejectRef = useRef<((err: Error) => void) | null>(null);
  const ensureSessionResolveRef = useRef<(() => void) | null>(null);
  const ensureSessionRejectRef = useRef<((err: Error) => void) | null>(null);
  const isMutedRef = useRef(false);
  const callStatusRef = useRef<JanusCallStatus>('idle');
  const subscribeToPublishersRef = useRef<(publishers?: VideoRoomPublisher[]) => void>(() => undefined);
  const lastRecoverLocalAudioAtRef = useRef(0);
  const recoverLocalAudioRunningRef = useRef(false);
  const speakerPlaybackWarnedRef = useRef(false);
  const reconnectCycleRef = useRef(0);
  const publisherIceConnectedNotifyRef = useRef<() => void>(() => undefined);
  const lastRepublishOutboundAtRef = useRef(0);

  useEffect(() => {
    recoverRemotePlaybackRef.current = async () => {
      const fn = options?.remotePlaybackRecoverRef?.current;
      if (!fn) return false;
      return fn();
    };
  });

  useEffect(() => {
    onAudioHardwareLostRef.current = options?.onAudioHardwareLost;
    onRemotePlaybackRecoveredRef.current = options?.onRemotePlaybackRecovered;
    onNetworkReconnectFailedRef.current = options?.onNetworkReconnectFailed;
    translateCallRef.current = options?.translateCall;
    onMediaConnectionLostRef.current = options?.onMediaConnectionLost;
    onRemoteCallEndedRef.current = options?.onRemoteCallEnded;
    onLocalAudioRecoveredRef.current = options?.onLocalAudioRecovered;
  }, [
    options?.onAudioHardwareLost,
    options?.onLocalAudioRecovered,
    options?.onMediaConnectionLost,
    options?.onRemoteCallEnded,
    options?.onRemotePlaybackRecovered,
    options?.onNetworkReconnectFailed,
    options?.remotePlaybackRecoverRef,
    options?.translateCall,
  ]);

  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  useEffect(() => {
    localStreamRef.current = localStream;
  }, [localStream]);

  const r: JanusCallRefs = {
    onAudioHardwareLostRef,
    translateCallRef,
    onMediaConnectionLostRef,
    onRemoteCallEndedRef,
    onLocalAudioRecoveredRef,
    onRemotePlaybackRecoveredRef,
    onNetworkReconnectFailedRef,
    recoverRemotePlaybackRef,
    mediaConnectionLostSignaledRef,
    deviceChangeTimerRef,
    wsReconnectAttemptsRef,
    wsReconnectTimerRef,
    signalingResyncInFlightRef,
    reconnectInFlightTimeoutRef,
    reconnectDebounceTimerRef,
    networkReconnectTimeoutRef,
    intentionalWsCloseRef,
    pendingAttachAfterCreateRef,
    ghostSessionIdRef,
    mediaReconnectPendingRef,
    scheduleWebSocketReconnectRef,
    reconnectWebSocketAndIceRef,
    recoverLocalAudioRef,
    refreshInboundMediaRef,
    wsRef,
    sessionIdRef,
    handleIdRef,
    publisherPcRef,
    publisherPendingCandidatesRef,
    isSessionReadyRef,
    micDisconnectHandledRef,
    isPublisherConfiguredRef,
    isPublisherJoinedRef,
    isJoinedRef,
    signalingUrlRef,
    roomIdRef,
    myPublisherIdRef,
    myPrivateIdRef,
    displayNameRef,
    subscriberFeedsRef,
    subscribedFeedIdsRef,
    networkDropPendingRef,
    pendingLocalStreamRefreshRef,
    networkBlipAtRef,
    pendingSubscriberAttachesRef,
    keepAliveIntervalRef,
    localStreamRef,
    joinRoomResolveRef,
    joinRoomRejectRef,
    ensureSessionResolveRef,
    ensureSessionRejectRef,
    isMutedRef,
    callStatusRef,
    subscribeToPublishersRef,
    lastRecoverLocalAudioAtRef,
    recoverLocalAudioRunningRef,
    speakerPlaybackWarnedRef,
    reconnectCycleRef,
    publisherIceConnectedNotifyRef,
    lastRepublishOutboundAtRef,
  };

  const set: JanusCallSetters = { setCallStatus, setLocalStream, setRemoteStream, setError };

  const sessionBagRef = useRef<JanusSessionBag>({ r, set });
  sessionBagRef.current = { r, set };

  const getSession = useCallback(() => sessionBagRef.current, []);

  return {
    r,
    set,
    getSession,
    callStatus,
    localStream,
    remoteStream,
    error,
    cleanupOnUnmount,
  };
}
